import { useMemo } from 'react'
import moment from 'moment'

import config from 'constants/config'
import { useAppSelector } from 'hooks/reduxHooks'
import { buildFlashCancellationOptionRows, type FlashCancellationOptionRow } from 'lib/offer/flashCancellationOptions'
import { getBasePackageUniqueKey } from 'lib/offer/offerUtils'
import { ALL_FLEXIBLE_CANCELLATION_POLICIES } from 'lib/offer/cancellationPolicyUtils'
import { CANCELLATION_POLICIES } from 'constants/cancellationPolicies'
import { isPackageEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'

const PURCHASE_CART_MODES: ReadonlySet<App.CheckoutCartMode> = new Set(['default', 'vertical-select'])

const PRESENTABLE_POLICIES: ReadonlySet<App.CancellationPolicyType | undefined> = new Set<App.CancellationPolicyType | undefined>([
  CANCELLATION_POLICIES.NON_REFUNDABLE as App.CancellationPolicyType,
  CANCELLATION_POLICIES.CASH_REFUND_PRIOR_TO_CHECK_IN_TEN_DAYS as App.CancellationPolicyType,
  ...ALL_FLEXIBLE_CANCELLATION_POLICIES,
])

interface Params {
  packageView: App.OfferPackageOptionView
  offer: App.HotelOffer
  allPackageViews: Array<App.OfferPackageOptionView>
  checkIn?: string
}

export interface FlashCancellationOptionRowsResult {
  baseKey: string
  views: Array<App.OfferPackageOptionView>
  rows: Array<FlashCancellationOptionRow>
  reserveForZeroKeys?: ReadonlySet<string>
  isPurchase: boolean
  showsOptions: boolean
}

export default function useFlashCancellationOptionRows(params: Params): FlashCancellationOptionRowsResult {
  const { packageView, offer, allPackageViews, checkIn } = params
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const isPurchase = useAppSelector(state => PURCHASE_CART_MODES.has(state.checkout.cart.mode ?? 'default'))

  const selectedKey = packageView.package.uniqueKey
  const baseKey = getBasePackageUniqueKey(packageView.package)
  const views = useMemo(
    () => allPackageViews.filter(view => getBasePackageUniqueKey(view.package) === baseKey),
    [allPackageViews, baseKey],
  )
  const rows = useMemo(() => buildFlashCancellationOptionRows(
    views,
    selectedKey,
    view => (canViewLuxPlusBenefits && view.memberPricing?.price) || view.pricing.price,
  ), [views, selectedKey, canViewLuxPlusBenefits])
  const reserveForZeroKeys = useMemo(() => {
    if (!config.RESERVE_FOR_ZERO_ENABLED && !isSpoofed) {
      return undefined
    }
    const checkInMoment = checkIn ? moment(checkIn) : undefined
    return new Set(rows
      .filter(row => isPackageEligibleForZeroReservation(offer, checkInMoment, row.policyType))
      .map(row => row.key))
  }, [rows, checkIn, isSpoofed, offer])

  return {
    baseKey,
    views,
    rows,
    reserveForZeroKeys,
    isPurchase,
    showsOptions: isPurchase && rows.length > 1 && rows.every(row => PRESENTABLE_POLICIES.has(row.policyType)),
  }
}
